import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Mail, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(null);   // null | true | false
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!email || !token) {
      setError("This link is missing an email or token. Please use the unsubscribe link from an email we sent you.");
      setLoading(false);
      return;
    }
    axios.get(`${API}/unsubscribe/status`, { params: { email, token } })
      .then((r) => setSubscribed(r.data.subscribed))
      .catch((e) => setError(e.response?.data?.detail || "Invalid or expired unsubscribe link."))
      .finally(() => setLoading(false));
  }, [email, token]);

  const handleUnsub = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/unsubscribe`, { email, token });
      setSubscribed(false);
      toast.success("You've been unsubscribed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const handleResub = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/resubscribe`, { email, token });
      setSubscribed(true);
      toast.success("Welcome back — you'll hear from us again.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF4F5] flex items-center justify-center px-4 py-16" data-testid="unsubscribe-page">
      <div className="max-w-lg w-full bg-white border border-[#E8DCDD] rounded-sm p-10">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3 text-center">
          Manage subscription
        </div>

        {loading ? (
          <div className="text-center py-6 text-[#6B5E62]">Loading…</div>
        ) : error ? (
          <div className="text-center py-4">
            <h1 className="text-2xl font-serif font-semibold text-[#2D2628] mb-3">Link not valid</h1>
            <p className="text-sm text-[#6B5E62] mb-6">{error}</p>
            <Link to="/" className="text-[#8C4A57] hover:underline text-sm font-semibold" data-testid="unsub-home-link">
              ← Return to Global Leader Solutions
            </Link>
          </div>
        ) : subscribed ? (
          <div>
            <h1 className="text-3xl font-serif font-semibold text-[#2D2628] mb-4 text-center leading-tight">
              Unsubscribe from Global Leader Solutions?
            </h1>
            <p className="text-sm text-[#6B5E62] leading-relaxed mb-6 text-center">
              Your subscribed address:
            </p>
            <div className="flex items-center justify-center gap-2 mb-8 text-[#2D2628] font-mono text-sm bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm px-4 py-3">
              <Mail className="w-4 h-4 text-[#8C4A57]" />
              <span data-testid="unsub-email-display">{email}</span>
            </div>
            <p className="text-sm text-[#6B5E62] leading-relaxed mb-6 text-center">
              You will stop receiving the free Leadership Guide, promotional updates, and course announcements.
              <br />
              <span className="text-[#8C4A57]">Transactional emails (order receipts, certificates) will continue.</span>
            </p>
            <button onClick={handleUnsub} disabled={busy}
              className="btn-primary w-full justify-center" data-testid="unsub-confirm-btn">
              {busy ? "Unsubscribing…" : "Yes, unsubscribe me"}
            </button>
            <div className="text-center mt-6">
              <Link to="/" className="text-xs text-[#6B5E62] hover:text-[#8C4A57] hover:underline" data-testid="unsub-cancel-link">
                Never mind — take me home
              </Link>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#FAF4F5] border border-[#E8DCDD] flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-[#8C4A57]" />
            </div>
            <h1 className="text-3xl font-serif font-semibold text-[#2D2628] mb-3 leading-tight">
              You have been unsubscribed.
            </h1>
            <p className="text-sm text-[#6B5E62] leading-relaxed mb-2">
              <span data-testid="unsub-email-display">{email}</span> will no longer receive our
              promotional emails.
            </p>
            <p className="text-sm text-[#6B5E62] leading-relaxed mb-8">
              Change your mind? You&apos;re welcome back any time.
            </p>
            <button onClick={handleResub} disabled={busy}
              className="inline-flex items-center px-5 py-2.5 border border-[#8C4A57] text-[#8C4A57] rounded-sm text-sm font-semibold uppercase tracking-widest hover:bg-[#FAF4F5] transition-colors"
              data-testid="unsub-resub-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              {busy ? "Re-subscribing…" : "Resubscribe"}
            </button>
            <div className="mt-8">
              <Link to="/" className="text-[#8C4A57] hover:underline text-sm font-semibold" data-testid="unsub-home-link">
                ← Return to Global Leader Solutions
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
